<template>
  <WizardModal
    v-model="currentStepIndex"
    v-model:show-modal="isVisible"
    :steps="wizardSteps"
    :modal-title="t('Check-In - Processo de Serviço')"
    :save-test-id="currentStepIndex === 3 ? 'checkout-confirm-btn' : ''"
    modal-size="modal-lg"
    modal-size-mobile="modal-lg"
    :show-complete="showComplete"
    custom-buttons
    :back-label="backButtonLabel"
    :back-icon="backButtonIcon"
    :next-label="nextButtonLabel"
    :next-icon="nextButtonIcon"
    :loading="isLoading || isFinalizing"
    @finish="handleFinish"
    @cancel="handleCancel"
    @close="handleClose"
  >
    <!-- Step 0: Início (Check-in) -->
    <template #step-0>
      <WizardStep0Inicio
        v-if="compromisso"
        :compromisso="compromisso"
        @next-step="handleStep0Next"
      />
    </template>

    <!-- Step 1: Antes (Before photos) -->
    <template #step-1>
      <WizardStep1Antes
        v-if="compromisso"
        :compromisso="compromisso"
        :can-add-service="true"
        @add-service="handleAddService"
        @refresh="loadWizardData"
      />

      <!-- WizardAddServicoModal is centralized in ModalManager.vue - opened via eventBus -->
      <!-- CompromissoServicoModal is centralized in ModalManager.vue - opened via eventBus -->
    </template>

    <!-- Step 2: Depois (After photos) -->
    <template #step-2>
      <WizardStep2Depois
        v-if="compromisso"
        :compromisso="compromisso"
        @refresh="loadWizardData"
      />
    </template>

    <!-- Step 3: Revisão (Review & finalization) -->
    <template #step-3>
      <WizardStep3Revisao
        v-if="compromisso"
        ref="step3Ref"
        :compromisso="compromisso"
        @next-step="handleFinalizationComplete"
      />
    </template>

    <!-- Complete: Sucesso (Success screen) -->
    <template #complete>
      <WizardStep4Sucesso @close="handleCompleteClose" />
    </template>
  </WizardModal>
  <!-- RecebimentoWizard is centralized in ModalManager.vue - opened via eventBus -->
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useWizardServicos } from '../../../composables/useWizardServicos';
import { eventBus } from '../../../eventBus';
import { log } from '../../../utils/DebugLogger';

import WizardModal from '../../../ui/WizardModal.vue';
import WizardStep0Inicio from './WizardStep0Inicio.vue';
import WizardStep1Antes from './WizardStep1Antes.vue';
import WizardStep2Depois from './WizardStep2Depois.vue';
import WizardStep3Revisao from './WizardStep3Revisao.vue';
import WizardStep4Sucesso from './WizardStep4Sucesso.vue';

interface WizardOpenData {
  compromissoId?: number;
  id?: number;
  [key: string]: unknown;
}

interface Props {
  show?: boolean;
  compromissoId?: number | null;
}

const props = withDefaults(defineProps<Props>(), {
  show: undefined,
  compromissoId: null
});

const emit = defineEmits<{
  'close': [];
  'complete': [];
  'update:show': [value: boolean];
}>();

const { t } = useI18n();
const {
  loadWizardData: loadData,
  calculateStage,
  canNavigateToStep
} = useWizardServicos();

// Internal state
const internalShow = ref<boolean>(false);
const internalCompromissoId = ref<number | null>(null);

// Wizard state
const currentStepIndex = ref<number>(0);
const compromisso = ref<any>(null);
const isLoading = ref<boolean>(false);
const showComplete = ref<boolean>(false);
const isFinalizing = ref<boolean>(false);

// Component refs
const step3Ref = ref<InstanceType<typeof WizardStep3Revisao> | null>(null);

// Computed visibility
const isVisible = computed((): boolean => {
  return props.show !== undefined ? props.show : internalShow.value;
});

// Computed compromisso ID
const currentCompromissoId = computed((): number | null => {
  return props.compromissoId !== null ? props.compromissoId : internalCompromissoId.value;
});

// Computed orcamento ID from compromisso
const orcamentoId = computed((): number => {
  return Number(compromisso.value?.compromisso_orcamento_id) || 0;
});

// Wizard steps configuration
const wizardSteps = computed(() => [
  {
    title: t('Início'),
    validate: (): boolean => true
  },
  {
    title: t('Fotos antes'),
    validate: (): boolean => {
      if (!compromisso.value) return false;
      return canNavigateToStep(1, 2, compromisso.value);
    }
  },
  {
    title: t('Fotos depois'),
    validate: (): boolean => {
      if (!compromisso.value) return false;
      return canNavigateToStep(2, 3, compromisso.value);
    }
  },
  {
    title: t('Revisar e finalizar'),
    validate: (): boolean => {
      if (isFinalizing.value) return false;
      const step3 = step3Ref.value;
      if (!step3 || !step3.canFinalize) return false;
      return step3.canFinalize;
    }
  }
]);

// Button configuration for WizardModal (function-based)
const backButtonLabel = computed(() => {
  return (step: number): string => {
    if (step === 0) return '';
    return t('Anterior');
  };
});

const backButtonIcon = computed(() => {
  return (step: number): string => {
    if (step === 0) return '';
    return 'pi pi-arrow-left';
  };
});

const nextButtonLabel = computed(() => {
  return (step: number): string => {
    if (step === 0) return '';
    if (step === 3) {
      return isFinalizing.value ? t('Finalizando...') : t('Recebido - Finalizar');
    }
    return t('Próximo');
  };
});

const nextButtonIcon = computed(() => {
  return (step: number): string => {
    if (step === 0) return '';
    if (step === 3) return 'pi pi-check';
    return 'pi pi-arrow-right';
  };
});

const emitWizardReady = async (reason = 'refresh'): Promise<void> => {
  await nextTick();
  eventBus.emit('wizard-servicos.ready', {
    compromissoId: Number(currentCompromissoId.value) || null,
    step: currentStepIndex.value,
    reason
  });
};

// Data loading with auto-resume
const loadWizardData = async (): Promise<void> => {
  if (!currentCompromissoId.value) return;
  try {
    log('WizardServicosModal', 'loadWizardData', 'Loading wizard data', { compromissoId: currentCompromissoId.value });
    isLoading.value = true;
    const data = await loadData(currentCompromissoId.value);
    compromisso.value = data as Record<string, unknown>;
    // The response contract types `compromisso` as a loose `object` until the
    // PHP-side ResponseContract is tightened. The runtime shape matches
    // CompromissoData (the composable owns the type), so the cast is safe.
    const calculatedStage = calculateStage(data as Parameters<typeof calculateStage>[0]);
    log('WizardServicosModal', 'loadWizardData', 'Calculated stage', { calculatedStage, currentStep: currentStepIndex.value });
    if (currentStepIndex.value === 0 && calculatedStage > 0) {
      currentStepIndex.value = calculatedStage;
    }
    eventBus.emit('wizard-servicos.refresh-photos', {});
    await emitWizardReady('data-loaded');
  } catch (error) {
    log('WizardServicosModal', 'loadWizardData', 'Error loading data', error);
    eventBus.emit('globalMessage.error', t('Erro ao carregar dados do serviço'));
  } finally {
    isLoading.value = false;
  }
};

// Step 0 manual advance (after check-in)
const handleStep0Next = async (): Promise<void> => {
  log('WizardServicosModal', 'handleStep0Next', 'Step 0 completed, advancing to step 1');
  await loadWizardData();
  currentStepIndex.value = 1;
};

// Handle finish (called when user clicks next on step 3)
const handleFinish = async (): Promise<void> => {
  try {
    log('WizardServicosModal', 'handleFinish', 'Starting finalization');
    isFinalizing.value = true;
    eventBus.emit('wizard-servicos.finalize');
  } catch (error) {
    log('WizardServicosModal', 'handleFinish', 'Error during finalization', error);
    isFinalizing.value = false;
  }
};

// Handle cancel
const handleCancel = (): void => {
  log('WizardServicosModal', 'handleCancel', 'Canceling wizard');
  handleClose();
};

// Open wizard programmatically (called by eventBus)
async function openWizard(data: WizardOpenData): Promise<void> {
  log('WizardServicosModal', 'openWizard', 'Opening wizard', data);
  currentStepIndex.value = 0;
  showComplete.value = false;
  isFinalizing.value = false;
  const compromissoId = Number(data?.compromissoId || data?.id);
  if (props.compromissoId !== null) {
    log('WizardServicosModal', 'openWizard', 'Controlled mode - parent should set compromissoId', { compromissoId });
  } else {
    internalCompromissoId.value = compromissoId;
  }
  await nextTick();
  await loadWizardData();
  if (props.show !== undefined) {
    emit('update:show', true);
  } else {
    internalShow.value = true;
  }
}

// Close wizard
function handleClose(): void {
  log('WizardServicosModal', 'handleClose', 'Closing wizard');
  if (props.show !== undefined) {
    emit('update:show', false);
  } else {
    internalShow.value = false;
  }
  if (props.compromissoId === null) {
    internalCompromissoId.value = null;
  }
  emit('close');
}

// Handle completion
function handleComplete(): void {
  log('WizardServicosModal', 'handleComplete', 'Wizard completed');
  handleClose();
  emit('complete');
  eventBus.emit('wizard-servicos.completed');
}

// Handle finalization completion (emitted by Step 3)
const handleFinalizationComplete = (): void => {
  log('WizardServicosModal', 'handleFinalizationComplete', 'Finalization completed, showing success screen');
  isFinalizing.value = false;
  showComplete.value = true;
};

// Handle a refused finalization (emitted by Step 3): unlock the button, stay on the review step.
const handleFinalizationFailed = (): void => {
  log('WizardServicosModal', 'handleFinalizationFailed', 'Finalization failed, staying on the review step');
  isFinalizing.value = false;
};

// Handle close from success screen
const handleCompleteClose = (): void => {
  log('WizardServicosModal', 'handleCompleteClose', 'Closing from success screen');
  showComplete.value = false;
  currentStepIndex.value = 0;
  compromisso.value = null;
  handleComplete();
};

// Nested modal handlers
const handleAddService = (): void => {
  log('WizardServicosModal', 'handleAddService', 'Opening add service modal via eventBus');
  eventBus.emit('modal.wizard-add-servico.open', {
    compromissoId: Number(currentCompromissoId.value),
    orcamentoId: orcamentoId.value,
    franquiaId: Number(compromisso.value?.compromisso_franquia_id) || 0
  });
};

// A service saved through CompromissoServicoModal (added from step 1 or edited
// from any step) changes the rows and the photo gates: reload while open.
const handleServiceSaved = async (): Promise<void> => {
  if (!isVisible.value) return;
  log('WizardServicosModal', 'handleServiceSaved', 'Service saved, reloading wizard data');
  await loadWizardData();
};

// Watch for compromisso ID changes (for controlled mode)
watch(() => currentCompromissoId.value, async (newId, oldId) => {
  if (newId && newId !== oldId && isVisible.value) {
    log('WizardServicosModal', 'watch:compromissoId', 'Compromisso ID changed, loading data', { newId, oldId });
    await loadWizardData();
  }
});

watch(currentStepIndex, async (newStep, oldStep) => {
  if (!isVisible.value || newStep === oldStep) return;
  await nextTick();
  eventBus.emit('wizard-servicos.step-changed', {
    compromissoId: Number(currentCompromissoId.value) || null,
    step: newStep,
    previousStep: oldStep
  });
  if (newStep !== 3) {
    eventBus.emit('wizard-servicos.ready', {
      compromissoId: Number(currentCompromissoId.value) || null,
      step: newStep,
      reason: 'step-changed'
    });
  }
});

// Sync wrappers for async eventBus handlers (EventCallback must return void)
const onWizardOpen = (data: unknown): void => { void openWizard(data as WizardOpenData); };
const onServiceSaved = (): void => { void handleServiceSaved(); };

// Event listeners
onMounted(() => {
  log('WizardServicosModal', 'onMounted', 'Component mounted');
  eventBus.on('wizard-servicos.open', onWizardOpen);
  eventBus.on('wizard-servicos.finalize-complete', handleFinalizationComplete);
  eventBus.on('wizard-servicos.finalize-failed', handleFinalizationFailed);
  eventBus.on('compromisso.servico.saved', onServiceSaved);
  if (isVisible.value && currentCompromissoId.value) {
    loadWizardData();
  }
});

onUnmounted(() => {
  log('WizardServicosModal', 'onUnmounted', 'Component unmounting');
  eventBus.off('wizard-servicos.open', onWizardOpen);
  eventBus.off('wizard-servicos.finalize-complete', handleFinalizationComplete);
  eventBus.off('wizard-servicos.finalize-failed', handleFinalizationFailed);
  eventBus.off('compromisso.servico.saved', onServiceSaved);
});

defineExpose({
  openWizard,
  handleClose
});
</script>

<style scoped>
/* Wizard-specific modal styling */
:deep(#modalWizardServicos .modal-dialog) {
  max-width: 1200px;
}

:deep(#modalWizardServicos .modal-body) {
  padding: 0;
  background-color: white;
}

@media (max-width: 768px) {
  :deep(#modalWizardServicos .modal-dialog) {
    width: 98%;
    margin: 10px auto;
  }
}
</style>
